import Router, { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import { callEnrolHQInit } from "@/features/cms/utils";

// Containers
import BaseSite from "@/containers/BaseSite";
import statusController from "@/features/applications/statusController";

// Forms
const TradeShow = () => import("@/features/trade_show/TradeShow");
const EnquiryView = () => import("@/features/enquiry/EnquiryView");
const EventsList = () =>
  import(/* webpackChunkName: "events" */ "@/features/events/EventsList");
const EventBooking = () =>
  import(/* webpackChunkName: "events" */ "@/features/events/EventBooking");
const EnrolmentView = () => import("@/features/enrolments/EnrolmentView");
const EnrolmentNonUserParentAcceptView = () =>
  import(
    "@/features/enrolments/non-user-parent-acceptance/EnrolmentNonUserParentAcceptView"
  );

// Parent Dashboard
const ParentDashboard = () => import("@/features/parent-dashboard/ParentDashboard");
const UserParentView = () => import("@/features/accept-offer/UserParentView");
const NonUserParentView = () => import("@/features/accept-offer/NonUserParentView");

// Print
const SingleReviewPrint = () =>
  import(/* webpackChunkName: "print" */ "@/features/review/print/SingleReviewPrint");
const BulkReviewPrint = () =>
  import(/* webpackChunkName: "print" */ "@/features/review/print/BulkReviewPrint");
const EnrolmentFormPrint = () =>
  import(/* webpackChunkName: "print" */ "@/views/EnrolmentFormPrint");
const EnquiryPrintView = () =>
  import(/* webpackChunkName: "print" */ "@/features/enquiry/EnquiryPrintView");
const BookingPrintView = () =>
  import(/* webpackChunkName: "print" */ "@/features/events/BookingPrintView");

// Views
const ChangeEmailPreferencesView = () =>
  import("@/features/email_preferences/ChangeEmailPreferencesView");
const PasswordResetConfirm = () => import("@/views/PasswordResetConfirm");
const NotFound = () => import("@/views/NotFound");
const PaymentView = () =>
  import(/* webpackChunkName: "payment" */ "@/features/payment/PaymentView");
const EmailVerification = () => import("@/views/EmailVerification");

// Other
const AttendeesLabelsPrintView = () =>
  import("@/features/events/AttendeesLabelsPrintView");
const CustomFormView = () => import("@/features/custom-form/CustomFormView");
const CustomFormPrintView = () => import("@/features/custom-form/CustomFormPrintView");
const HijackTokenView = () => import("@/features/hijack/HijackTokenView");
const SAMLAuthenticationView = () =>
  import(/* webpackChunkName: "saml" */ "@/features/saml/SAMLAuthenticationView");

const routerConfig = {
  ...(process.env.VUE_APP_COMPAT ? { history: createWebHistory() } : { mode: "history" }),
  linkActiveClass: "open active",
  scrollBehavior: () => (process.env.VUE_APP_COMPAT ? { top: 0 } : { y: 0 }),
  routes: [
    {
      path: "/",
      name: "",
      component: BaseSite,
      meta: { disallowStaff: true },
      redirect: {
        // The check is for jest tests
        name: window.SchoolSettings
          ? window.SchoolSettings.home_page_redirect_route_name
          : "Enquiry",
      },
      children: [
        {
          path: "change-email-preferences/:token/",
          name: "ChangeEmailPreferences",
          component: ChangeEmailPreferencesView,
        },
        {
          path: "password-reset/confirm/:uid/:token/",
          name: "PasswordResetConfirm",
          component: PasswordResetConfirm,
        },
        {
          path: "email-verification/:token/",
          name: "EmailVerification",
          component: EmailVerification,
        },
        {
          path: "events",
          name: "Events",
          component: EventsList,
        },
        {
          path: "events/:slug/:publicToken?",
          name: "Event Booking",
          component: EventsList,
        },
        {
          path: "embed/events",
          name: "Embedded Events",
          component: EventsList,
          props: { isEmbedView: true },
        },
        {
          path: "embed/events/:slug/:publicToken?",
          name: "Embedded Event Booking",
          component: EventsList,
          props: { isEmbedView: true },
        },
        {
          path: "event-booking/:slug/:session/:publicToken?",
          name: "Session Booking",
          component: EventBooking,
        },
        {
          path: "embed/event-booking/:slug/:session/:publicToken?",
          name: "Embedded Session Booking",
          component: EventBooking,
          props: { isEmbedView: true },
        },
        {
          path: "enquiry",
          name: "Enquiry",
          // The check is for jest tests
          component: (
            window.SchoolSettings
              ? window.SchoolSettings.is_enquiry_enabled_for_parent // Hide route if enquiry is disabled
              : true
          )
            ? EnquiryView
            : NotFound,
        },
        {
          path: "embed/enquiry",
          name: "Embedded Enquiry",
          // The check is for jest tests
          component: (
            window.SchoolSettings
              ? window.SchoolSettings.is_enquiry_enabled_for_parent // Hide route if enquiry is disabled
              : true
          )
            ? EnquiryView
            : NotFound,
          props: { isEmbedView: true },
        },
        {
          path: "register-your-interest",
          name: "TradeShow",
          component: TradeShow,
        },
        {
          path: "embed/register-your-interest",
          name: "Embedded TradeShow",
          component: TradeShow,
          props: { isEmbedView: true },
        },
        {
          path:
            "student/" +
            // The check is for jest tests
            (window.SchoolSettings ? window.SchoolSettings.gpa_settings.url_path : "gpa"),
          name: "General Purpose Application",
          component: statusController.isStatusEnabled(statusController.GPA)
            ? EnrolmentView
            : NotFound,
          props: { metafieldsFormCode: "eoi" },
        },
        {
          // The check is for jest tests
          path:
            "student/" +
            (window.SchoolSettings
              ? window.SchoolSettings.enrolment_settings.form_url_path
              : "enrolment") +
            "/second-parent/sign/:token/",
          name: "Enrolment Non User Parent Acceptance",
          component: EnrolmentNonUserParentAcceptView,
          props: { metafieldsFormCode: "enr" },
        },
        {
          // The check is for jest tests
          path:
            "student/" +
            (window.SchoolSettings
              ? window.SchoolSettings.gpa_settings.url_path
              : "gpa") +
            "/second-parent/sign/:token/",
          name: "GPA Non User Parent Acceptance",
          component: EnrolmentNonUserParentAcceptView,
          props: { metafieldsFormCode: "eoi" },
        },
        {
          // The check is for jest tests
          path:
            "student/" +
            (window.SchoolSettings
              ? window.SchoolSettings.enrolment_settings.form_url_path
              : "enrolment"),
          name: "Enrolment",
          component: EnrolmentView,
          props: { metafieldsFormCode: "enr" },
        },
        {
          path: "payment/:id/:paymentKind/",
          name: "Payment",
          component: PaymentView,
        },
        {
          path: "parent/dashboard/:profileId?",
          name: "Parent Dashboard",
          component: ParentDashboard,
        },
        {
          path: "accept-offer/first-parent/:id/:offerKind(enrolment_offer|reserved_place_offer)",
          name: "Accept Offer Primary Parent",
          component: UserParentView,
        },
        {
          path: "accept-offer/second-parent/:token/:offerKind(enrolment_offer|reserved_place_offer)",
          name: "Accept Offer Secondary Parent",
          component: NonUserParentView,
        },
        {
          path: "/form/:formUrlPath",
          name: "Custom Form",
          component: CustomFormView,
          props: { isPreview: false },
        },
      ].filter((route) => route),
    },
    {
      path: "/print/",
      name: "",
      component: BaseSite,
      children: [
        {
          path: "eoi/:token/:id/",
          name: "General Purpose Application Print",
          component: EnrolmentFormPrint,
          props: { isPrintView: true, metafieldsFormCode: "eoi" },
        },
        {
          path: "enrolment/:token/:id/",
          name: "Enrolment Print",
          component: EnrolmentFormPrint,
          props: { isPrintView: true, metafieldsFormCode: "enr" },
        },
        {
          path: "enrolment_latest_updated/:token/:id/",
          name: "Enrolment Staff Re-Print",
          component: EnrolmentFormPrint,
          props: { isPrintView: true, metafieldsFormCode: "enr" },
        },
        {
          path: "enrolment_incomplete_staff/:token/:id/",
          name: "Incomplete Enrolment Staff Re-Print",
          component: EnrolmentFormPrint,
          props: { isPrintView: true, metafieldsFormCode: "enr" },
        },
        {
          path: "enquiry/:token/:id/:enquiryId/",
          name: "Enquiry Print",
          component: EnquiryPrintView,
          props: { isPrintView: true },
        },
        {
          path: "event_booking/:token/:id/:bookingId/",
          name: "Event Booking Print",
          component: BookingPrintView,
          props: { isPrintView: true },
        },
        {
          // TODO: remove
          path: "accept_offer/:token/:id",
          redirect: "accept_offer/:token/:id/enrolment_offer",
        },
        {
          path: "accept_offer/:token/:id/:offerKind(enrolment_offer|reserved_place_offer)",
          name: "Accept Offer Print",
          component: UserParentView,
          props: { isPrintView: true },
        },
        {
          path: "enrolment_review/:token/:id/:interviewId?/",
          name: "Enrolment Review Print",
          component: SingleReviewPrint,
          props: {
            isPrintView: true,
            title:
              statusController.getStatusLabel(statusController.ENROLMENT) + " Review",
          },
        },
        {
          path: "gpa_review/:token/:id/:interviewId?/",
          name: "GPA Review Print",
          component: SingleReviewPrint,
          props: {
            isPrintView: true,
            title: `${statusController.getStatusLabel(statusController.GPA)} Review`,
          },
        },
        {
          path: "form/:token/:id/:formSubmitId/",
          name: "Custom Form Print",
          component: CustomFormPrintView,
          props: { isPrintView: true },
        },
      ],
    },
    {
      path: "/staff-print/bulk_review_print/:date/",
      name: "Bulk Enrolment Review Print",
      component: BulkReviewPrint,
      props: {
        isPrintView: true,
        title: statusController.getStatusLabel(statusController.ENROLMENT) + " Review",
      },
      meta: { disallowStaff: false },
    },
    {
      path: "/form/preview/:formUrlPath",
      name: "Custom Form Preview",
      component: CustomFormView,
      props: { isPreview: true },
      meta: { disallowStaff: false },
    },
    {
      path: "/print/attendees_labels/:id/",
      name: "Attendees Labels Print",
      component: AttendeesLabelsPrintView,
    },
    {
      path: "/hijack-token/:email",
      name: "Hijack Token",
      component: HijackTokenView,
    },
    {
      path: "/auth-session/",
      name: "Authenticate Session",
      component: SAMLAuthenticationView,
    },
    {
      path: process.env.VUE_APP_COMPAT ? "/:pathMatch(.*)*" : "*",
      name: "Not Found",
      component: NotFound,
    },
  ],
};

const router = process.env.VUE_APP_COMPAT
  ? createRouter(routerConfig)
  : new Router(routerConfig);

router.beforeEach((to, _from, next) => {
  if (to.matched.some((record) => record.meta.disallowStaff) && store.getters.isStaff) {
    window.location = "/dashboard/";
    return;
  }
  if (window.enrolHQClientUpdateRequired) {
    window.location = to.fullPath;
    return;
  }
  next();
});

router.afterEach((to) => {
  callEnrolHQInit("enrolHQInitAlways");
  if (to.name === "Parent Dashboard") {
    callEnrolHQInit("enrolHQInitParentDashboard");
  }
});

export { Router };
export default router;
